// ---------------------------------------------------------------------------
// SSN / ITIN validation (ported from dealer-ui formValidations.ts)
// ---------------------------------------------------------------------------
// Operates on a canonical digits-only string. IRSN handling and the test-dealer
// bypass from dealer-ui are intentionally dropped (no equivalent here).

// Known-invalid placeholder/sequential numbers rejected outright.
const SSN_BLACKLIST = new Set([
  "219099999",
  "078051120",
  "012345678",
  "123456789",
  "987654321",
  "876543210",
]);

/**
 * Determines whether a 9-digit string is a valid ITIN (number starting with 9
 * whose middle group falls in the IRS-assigned ITIN ranges).
 * @param {string} ssn - The 9-digit string (digits only).
 * @returns {boolean} True when the value matches ITIN group ranges.
 */
function isSsnAnItin(ssn: string): boolean {
  if (!ssn.startsWith("9")) {
    return false;
  }
  const middle = Number(ssn.slice(3, 5));
  if (middle === 89 || middle === 93) {
    return false;
  }
  return (
    (middle >= 70 && middle <= 88) ||
    (middle >= 90 && middle <= 92) ||
    (middle >= 94 && middle <= 99) ||
    (middle >= 50 && middle <= 65)
  );
}

/**
 * Validates a SSN or ITIN. Accepts any string; non-digits are stripped first.
 * Mirrors dealer-ui's isValidSsnOrItin (minus IRSN / test-dealer handling):
 * exactly 9 digits, not all-same, not blacklisted, not 98765432x, area not
 * 000/666, group not 00, serial not 0000; numbers starting with 9 must be ITINs.
 * Lower environments allow a 666 area (remapped to 006) for test data.
 * @param {string} value - The SSN/ITIN value (may contain separators).
 * @returns {boolean} True when the value is a valid SSN or ITIN.
 */
export function isValidSsnOrItin(value: string): boolean {
  let ssnString = value.replaceAll(/\D/g, "");

  // Must be exactly 9 digits.
  if (!/^\d{9}$/.test(ssnString)) {
    return false;
  }
  // Reject all-repeated digits (e.g. 111111111).
  if (/^(\d)\1{8}$/.test(ssnString)) {
    return false;
  }
  if (SSN_BLACKLIST.has(ssnString)) {
    return false;
  }
  // Reject any starting with 98765432 + any digit.
  if (/^98765432\d$/.test(ssnString)) {
    return false;
  }
  // Production rejects a 666 area; lower environments remap it so test data with
  // a 666 prefix is accepted.
  if (!import.meta.env.PROD) {
    ssnString = ssnString.replace(/^666/, "006");
  }

  return ssnString.startsWith("9")
    ? isSsnAnItin(ssnString) && /^(?!666|000)\d{3}(?!00)\d{6}$/.test(ssnString)
    : /^(?!666|000)\d{3}(?!00)\d{2}(?!0000)\d{4}$/.test(ssnString);
}
